import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Search, UserPlus } from "lucide-react";
import UserCard from "../../../ui/UserCard";
import { useUsers } from "../../../../context/UserContext";
import { useTopBar } from "../../../../context/TopBarContext";
import ItemListPopup from "../../../ui/ItemListPopup";
import CustomButton from "../../../ui/CommonButton";
import ToastContainer, { useToast } from "../../../ui/Toast_Message";
import { ReactComponent as ArrowUp } from "../../../../assets/icons/svg/arrow-up.svg";
import { ReactComponent as ArrowDown } from "../../../../assets/icons/svg/arrow-down.svg";
import { User } from "../../../../context/UserContext";

type UserRole = "admin" | "user";

const UsersListScreen: React.FC = () => {
  const { users, addUser, updateUser } = useUsers();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const { showToast, toasts } = useToast();
  const location = useLocation();

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    const title = "Users";
    const showBack = false;

    setTitle(title);
    setShowBackButton(showBack);

    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname]);

  const filteredUsers = users.filter((user) =>
    user.name.toLowerCase().includes(search.toLowerCase()) ||
    user.email.toLowerCase().includes(search.toLowerCase())
  );

  const roleOptions = [
    { label: "Admin", value: "admin" as const },
    { label: "User", value: "user" as const },
  ];

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const resetForm = () => {
    setUserName("");
    setUserEmail("");
    setUserRole(null);
    setDropdownOpen(false);
    setEmailError(null);
    setEditingUser(null);
  };

  const handleAddUser = () => {
    resetForm();
    setShowPopup(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserName(user.name);
    setUserEmail(user.email);
    setUserRole(user.role);
    setShowPopup(true);
  };

  const handleCancel = () => {
    resetForm();
    setShowPopup(false);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setUserEmail(email);
    
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = () => {
    if (userName && userEmail && userRole && !emailError) {
      // Check if email already exists (but allow current user's email when editing)
      const emailExists = users.some(user => 
        user.email.toLowerCase() === userEmail.toLowerCase() && 
        (!editingUser || user.id !== editingUser.id)
      );
      
      if (emailExists) {
        setEmailError("This email is already registered");
        return;
      }

      if (editingUser) {
        // Update existing user
        updateUser(editingUser.id, {
          name: userName,
          email: userEmail,
          role: userRole,
        });

        showToast(
          "success",
          "",
          "Updated successfully!",
          `user-update-success-${Date.now()}`
        );
      } else {
        // Add new user
        addUser({
          name: userName,
          email: userEmail,
          role: userRole,
        });

        showToast(
          "success",
          "",
          "Added successfully!",
          `user-add-success-${Date.now()}`
        );
      }
      
      resetForm();
      setShowPopup(false);
    }
  };

  const isFormValid = userName && userEmail && userRole && !emailError;

  return (
    <>
      <div className="min-h-screen bg-white py-4 pl-6 pr-2 mt-1">
        <div className="mx-auto">
          {/* Top Bar */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-sm 2xl:text-base 4xl:text-lg font-medium text-gray-900">
              Total {users.length < 10 ? "0" : ""}{users.length} user{users.length !== 1 ? "s" : ""}
            </h2>

            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 3xl:w-6 3xl:h-6" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search users"
                  className="w-64 pl-10 pr-4 3xl:pl-12 py-2.5 rounded-[8px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-sm placeholder:font-medium"
                />
              </div>

              <button
                onClick={handleAddUser}
                className="inline-flex items-center justify-center gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-4 py-2.5 rounded-md font-xs"
              >
                <UserPlus className="w-4 h-4" />
                Add user
              </button>
            </div>
          </div>

          {/* Filtered Content */}
          {filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-gray-400 text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No users found
                </h3>
                <p className="text-gray-500 mb-4">
                  Try uploading or changing filters
                </p>
                <button
                  onClick={handleAddUser}
                  className="inline-flex items-center justify-center gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-3 py-2 rounded-md font-xs"
                >
                  <UserPlus className="w-4 h-4" />
                  Add user
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              

              {/* Users Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4  4xl:grid-cols-5 gap-6 justify-items-center">
                {filteredUsers.map((user) => (
                  <UserCard key={user.id} user={user} onEdit={handleEditUser} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Popup Overlay */}
      {showPopup && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
          <ItemListPopup title={editingUser ? "Edit user" : "Add user"} onClose={handleCancel}>
            <div className="flex flex-col h-full">
              <div className="flex-1 min-h-0 px-1  space-y-4">
                {/* Email Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={userEmail}
                    onChange={handleEmailChange}
                    placeholder="Enter email"
                    className="w-full px-3 py-2 border rounded-[8px] text-sm border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  {emailError && (
                    <p className="text-xs text-[#DB2A2A] mt-1 ml-1">
                      {emailError}
                    </p>
                  )}
                </div>

                {/* Name Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Enter user name"
                    maxLength={50}
                    className="w-full px-3 py-2 border rounded-[8px] text-sm border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>

                {/* User Role Dropdown */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User role
                  </label>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-[8px] text-left text-sm bg-white flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-blue-400"
                  >
                    <span
                      className={userRole ? "text-black" : "text-gray-400"}
                    >
                      {roleOptions.find((r) => r.value === userRole)?.label || "Select role"}
                    </span>
                    <span>{dropdownOpen ? <ArrowUp /> : <ArrowDown />}</span>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute z-10 mt-2 w-full rounded-lg bg-white border border-gray-200 shadow-lg">
                      {roleOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setUserRole(option.value);
                            setDropdownOpen(false);
                          }}
                          className={`px-4 py-2 cursor-pointer rounded-md mx-1 my-1 hover:bg-blue-50 text-sm ${
                            userRole === option.value
                              ? "bg-blue-100 font-medium"
                              : "text-gray-700"
                          }`}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-auto">
                <hr className="border-t border-gray-200 my-4" />
                <div className="flex justify-center gap-3">
                  <CustomButton
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton
                    variant="default"
                    isActive={!!isFormValid}
                    onClick={handleSubmit}
                    className="flex-1"
                  >
                    {editingUser ? "Update" : "Add"}
                  </CustomButton>
                </div>
              </div>
            </div>
          </ItemListPopup>
        </>
      )}
      <ToastContainer toasts={toasts} />
    </>
  );
};

export default UsersListScreen;